import { useEffect, useMemo, useRef, useState } from "react";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import "Leaflet.Deflate";
import { CustomTooltip } from "components/LeafletMap/CustomTooltip/CustomTooltip";
import { TRACK_COLORS } from "config/constants";
import { usePublicTracks } from "hooks/usePublicTracks";
import { useUserTracks } from "hooks/useUserTracks";
import { PathOptions, Polyline } from "leaflet";
import L from "leaflet";
import escape from "lodash/escape";
import { nanoid } from "nanoid";
import { useMap } from "react-leaflet";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import { Coords, TrackWithMetadata } from "types/app";
import { formatDistance } from "utils/formatters/formatDistance";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "components/LeafletMap/MarkerCluster.Custom.css";

type GetPolylineOptions = {
  trackpoints: Coords[];
  color?: PathOptions["color"];
};
const getPolyline = ({ trackpoints, color }: GetPolylineOptions) => {
  if (trackpoints.length === 0) return undefined;
  return L.polyline(trackpoints, {
    color: color ?? TRACK_COLORS.public,
    weight: 4,
    opacity: 1,
    smoothFactor: 1,
  });
};

type TooltipData = {
  polyline: Polyline;
  name: string;
  length: number | undefined;
};

export const UserTracksContainer = () => {
  const { tracks, hiddenTrackIds, selectTrack, selectedTrack, leaveOpenTrack } =
    useUserTracks();
  const { tracks: publicTracks, selectTrack: selectPublicTrack } =
    usePublicTracks();
  const {
    dispatch: dispatchOptions,
    state: { isLeftMenuOpen, units },
  } = useOptions();
  const {
    state: { trackWaypoints },
  } = useRouting();
  const map = useMap();
  const [tooltipsData, setTooltipsData] = useState<TooltipData[]>([]);
  const selectedTrackRef = useRef(selectedTrack);
  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);
  const isSmallViewport = useIsSmallViewport();

  useEffect(() => {
    if (!map) return;
    map.createPane("clusterMarkerPane").style.zIndex = "610";
  }, [map]);

  useEffect(() => {
    if (!map || selectedTrackRef.current || trackWaypoints.length !== 0)
      return undefined;
    const markerClusterUserTracks = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: false,
      // maxClusterRadius: 80,
      clusterPane: "clusterMarkerPane",
    });

    const deflatedUserTracks = L.deflate({
      minSize: 100,
      markerType: L.circleMarker,
      markerLayer: markerClusterUserTracks,
      // greedyCollapse: false,
    });

    const userTooltipData: TooltipData[] = [];

    if (tracks) {
      const visibileTracks: TrackWithMetadata[] = [];

      for (const track of tracks) {
        let visible = true;
        for (const hiddenTrackId of hiddenTrackIds) {
          if (hiddenTrackId === track.id) {
            visible = false;
            break;
          }
        }
        if (visible) {
          visibileTracks.push(track);
        }
      }

      visibileTracks?.forEach((track) => {
        const polyline = getPolyline({
          trackpoints: track.trackPoints,
          color: track.color,
        });
        if (!polyline) return;
        userTooltipData.push({
          polyline,
          name: track.name,
          length: track.meta?.length,
        });
        polyline.on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          if (isLeftMenuOpen && isSmallViewport) {
            dispatchOptions(
              updateNonSaveableOptions({ isLeftMenuOpen: false })
            );
          }
          leaveOpenTrack(() => selectTrack(track.id));
        });
        polyline.addTo(deflatedUserTracks);
      });
    }

    const publicTooltipData: TooltipData[] = [];

    if (publicTracks) {
      publicTracks.forEach((track) => {
        const polyline = getPolyline({ trackpoints: track.trackPoints });

        if (!polyline) return;

        publicTooltipData.push({
          polyline,
          name: track.name,
          length: track.meta?.length,
        });
        polyline.on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          // Opens the track in the editor (and fits it to the screen), after asking if an edited track would be lost
          selectPublicTrack(track.id);
        });
        polyline.addTo(deflatedUserTracks);
      });
    }
    setTooltipsData([...userTooltipData, ...publicTooltipData]);

    deflatedUserTracks.addTo(map);

    return () => {
      deflatedUserTracks.removeFrom(map);
    };
  }, [
    dispatchOptions,
    isLeftMenuOpen,
    isSmallViewport,
    map,
    publicTracks,
    selectTrack,
    leaveOpenTrack,
    selectPublicTrack,
    trackWaypoints.length,
    tracks,
    hiddenTrackIds,
  ]);

  const tooltips = useMemo(
    () =>
      tooltipsData.map((data) => (
        <CustomTooltip
          key={nanoid()}
          polyline={data.polyline}
          // Track names are user content (incl. other users' public tracks), so they must be escaped
          html={`<div style="text-align:center"><div>${escape(data.name)}</div><div>
          ${
            data.length ? formatDistance(data.length * 1000, units, 1) : ""
          }</div></div>`}
        />
      )),
    [tooltipsData, units]
  );
  return useMemo(
    () =>
      tooltips.length > 0 && !selectedTrackRef.current ? <>{tooltips}</> : null,
    [tooltips]
  );
};
